import { CaseWorkerPreferencesDTO } from '@repo/shared/dtos/caseWorker'

import { ICaseWorkerPreferencesApi } from '@/spi/api/case-worker-preferences'

const CASE_WORKER_PREFERENCES_LOCAL_STORAGE_KEY = 'caseWorkerPreferences'

type CaseWorkerPreferencesByOrganization = {
  [organizationId: string]: CaseWorkerPreferencesDTO
}

const DEFAULT_PREFERENCES: CaseWorkerPreferencesDTO = {
  beneficiaryListSortOption: { field: 'fullName', direction: 'ASC' },
  beneficiaryListColumnIds: ['profile'],
}

export const buildCaseWorkerPreferencesApi = (): ICaseWorkerPreferencesApi => {
  return {
    findOne: async (organizationId: string) => {
      return new Promise((resolve, reject) => {
        try {
          handleLegacyBeneficiaryListColumnIds(organizationId)

          resolve(loadCaseWorkerPreferences(organizationId))
        } catch (e) {
          console.warn('Failed to get user preferences', e)
          reject(new Error('Failed to get user preferences'))
        }
      })
    },
    updateOne: async (organizationId: string, caseWorkerPreferences: CaseWorkerPreferencesDTO) => {
      return new Promise((resolve, reject) => {
        try {
          const preferencesByOrganization = loadCaseWorkerPreferencesByOrganization() ?? {}
          preferencesByOrganization[organizationId] = caseWorkerPreferences
          localStorage.setItem(
            CASE_WORKER_PREFERENCES_LOCAL_STORAGE_KEY,
            JSON.stringify(preferencesByOrganization),
          )
          resolve()
        } catch (e) {
          console.warn('Failed to persist user preferences', e)
          reject(new Error('Failed to persist user preferences'))
        }
      })
    },
  }
}

function loadCaseWorkerPreferences(organizationId: string): CaseWorkerPreferencesDTO {
  const preferencesByOrganization = loadCaseWorkerPreferencesByOrganization()
  return preferencesByOrganization?.[organizationId] ?? DEFAULT_PREFERENCES
}

function loadCaseWorkerPreferencesByOrganization(): CaseWorkerPreferencesByOrganization | null {
  const rawPreferencesByOrganization = localStorage.getItem(
    CASE_WORKER_PREFERENCES_LOCAL_STORAGE_KEY,
  )
  if (!rawPreferencesByOrganization) {
    return null
  }
  return JSON.parse(rawPreferencesByOrganization) as CaseWorkerPreferencesByOrganization
}

// TODO: remove this once we estimated that everyone has migrated to the new system
function handleLegacyBeneficiaryListColumnIds(organizationId: string): void {
  try {
    const rawColumnIds = localStorage.getItem(`columnIds_${organizationId}`)
    if (!rawColumnIds) return
    const columnIds = JSON.parse(rawColumnIds) as string[]

    const preferencesByOrganization = loadCaseWorkerPreferencesByOrganization() ?? {}
    preferencesByOrganization[organizationId] = {
      ...DEFAULT_PREFERENCES,
      ...(preferencesByOrganization[organizationId] ?? {}),
      beneficiaryListColumnIds: columnIds,
    }

    localStorage.setItem(
      CASE_WORKER_PREFERENCES_LOCAL_STORAGE_KEY,
      JSON.stringify(preferencesByOrganization),
    )

    localStorage.removeItem(`columnIds_${organizationId}`)
  } catch (e) {
    console.warn('Failed to get legacy beneficiary list column ids', e)
  }
}
